import { Component, Input } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  AverageQueryMeteredDataResultDto,
  MeterStandType,
  ObservationTimeSeriesPeriod,
  QueryMeteredDataResultDto
} from '@app/api/dmc';
import { TranslatePipe } from '@ngx-translate/core';
import { InputNumberDirective } from '@shared/components/input/number/input-number.directive';

@Component({
  selector: 'app-timeseries-form',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TranslatePipe,
    InputNumberDirective,
    DecimalPipe,
  ],
  templateUrl: './timeseries-form.component.html',
  styleUrl: './timeseries-form.component.css'
})
export class TimeseriesFormComponent {
  @Input() public timeSeries?: QueryMeteredDataResultDto[];
  @Input() public timeSeriesAverage?: AverageQueryMeteredDataResultDto[];

  /** Column Array **/
  public columns: string[] = [];

  protected readonly ReadingType = MeterStandType;
  protected readonly ObservationTimeSeriesPeriod = ObservationTimeSeriesPeriod;

  /** Value form **/
  public valueForm: FormGroup = new FormGroup({
    start: new FormControl(null, [Validators.required]),
    end: new FormControl(null, [Validators.required]),
    periodUsage: new FormControl(0, [Validators.required]),
    distributionType: new FormControl(null, [Validators.required]),
  });

  public areAllMeasured(): boolean {
    if (!this.timeSeries || this.timeSeries.length === 0) return false;
    return this.timeSeries.every(t => t.consumptionType === this.ReadingType.Ok);
  }

  public toggleAllMeasured(event: Event): void {
    if (!this.timeSeries) return;

    const checkbox = event.target as HTMLInputElement;
    const isChecked: boolean = checkbox?.checked ?? !this.areAllMeasured();

    for (const t of this.timeSeries) {
      t.consumptionType = isChecked ? this.ReadingType.Ok : undefined;
    }
  }

  public getTimeRange(s: string, resolution?: string): string {
    const d = new Date(s);
    const hours = d.getHours();
    const minutes = d.getMinutes();

    // Determine the interval based on resolution
    const isQuarter = resolution === ObservationTimeSeriesPeriod.Quarter || resolution === 'Quarter';

    // Format start time
    const fromHours = hours < 10 ? '0' + hours : hours.toString();
    const fromMinutes = minutes < 10 ? '0' + minutes : minutes.toString();

    // Calculate end time
    let toHours: number;
    let toMinutes: number;

    if (isQuarter) {
      // Add 15 minutes for quarter intervals
      toMinutes = minutes + 15;
      toHours = hours;

      if (toMinutes >= 60) {
        toMinutes = 0;
        toHours = hours + 1;
      }
    } else {
      // Add 1 hour for hourly intervals
      toHours = hours + 1;
      toMinutes = minutes;
    }

    // Handle midnight rollover
    if (toHours >= 24) {
      toHours = 0;
    }

    // Format end time
    const toHoursFormatted = toHours < 10 ? '0' + toHours : toHours.toString();
    const toMinutesFormatted = toMinutes < 10 ? '0' + toMinutes : toMinutes.toString();

    return `${fromHours}:${fromMinutes} - ${toHoursFormatted}:${toMinutesFormatted}`;
  }

  public isEditable(t: QueryMeteredDataResultDto): boolean {
    // Only disable if readingType is Ok AND consumptionType is also Ok
    // This allows editing when readingType is Ok but consumption is null/missing
    return !(t.readingType === this.ReadingType.Ok && t.consumptionType === this.ReadingType.Ok);
  }

  /** Sum of all consumption values in the visible rows. */
  public getTotalConsumption(): number {
    if (!this.timeSeries) return 0;
    return this.timeSeries.reduce((sum, t) => sum + Number(t.consumption ?? 0), 0);
  }

  /** Sum of all estimated consumption values when the average column is shown. */
  public getTotalEstimated(): number {
    if (!this.timeSeriesAverage) return 0;
    return this.timeSeriesAverage.reduce((sum, t) => sum + Number(t.consumption ?? 0), 0);
  }
}
